import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CargoProblemEventKind } from '@magimp/shared';
import {
  EmptyState,
  ErrorState,
  JournalTimeline,
  LoadingState,
  type JournalTimelineEntry,
} from '../../../ui/index.js';
import { formatDateTime, type AppLocale } from '../../../i18n/index.js';
import { useCargoProblemLabels } from '../labels.js';
import { useCargoProblemActivity } from '../queries.js';

/**
 * WHY: writing a message and attaching a file are the conversation itself, and
 * both already render there as a message row or as a card. Listing them again
 * would make this panel a second, duplicate copy of the thread instead of the
 * technical trail. A file DELETION stays: it is a moderation act, not content.
 */
const CONVERSATION_KINDS: readonly CargoProblemEventKind[] = ['COMMENT_ADDED', 'FILE_UPLOADED'];

/**
 * @aiSecurity technical trail only: actor, action, time and status movement.
 *   Message bodies and file names stay in the conversation and its library —
 *   repeating them here would rebuild the content log the audit deliberately
 *   is not.
 */
export function CargoProblemAuditSection({ problemId }: { problemId: string }) {
  const { t, i18n } = useTranslation('cargoProblems');
  const locale = i18n.language as AppLocale;
  const labels = useCargoProblemLabels();
  const activity = useCargoProblemActivity(problemId);

  const entries = useMemo<JournalTimelineEntry[]>(
    () =>
      (activity.data?.items ?? [])
        .filter((item) => !CONVERSATION_KINDS.includes(item.kind))
        .map((item) => ({
          key: item.id,
          primary: <strong>{labels.event(item.kind)}</strong>,
          metadata: (
            <>
              <span>{item.actor.name}</span>
              <span>·</span>
              <time dateTime={item.createdAt}>{formatDateTime(item.createdAt, locale)}</time>
              {item.metadata.from && item.metadata.to ? (
                <>
                  <span>·</span>
                  <span>
                    {labels.status(item.metadata.from)} → {labels.status(item.metadata.to)}
                  </span>
                </>
              ) : null}
            </>
          ),
        })),
    [activity.data, labels, locale],
  );

  return (
    <details className="cargo-problem-audit">
      <summary>
        {t('timeline.title')}
        <span>{t('timeline.hint')}</span>
      </summary>
      {activity.isPending ? <LoadingState /> : null}
      {activity.isError ? (
        <ErrorState retryLabel={t('workspace.retry')} onRetry={() => void activity.refetch()} />
      ) : null}
      {activity.data && entries.length === 0 ? <EmptyState title={t('timeline.empty')} /> : null}
      {entries.length > 0 ? <JournalTimeline entries={entries} /> : null}
    </details>
  );
}
